import React, { useEffect, useState } from 'react'

import { FadeSwap, BodyText, ProductGrid, BackToTop, Notification } from '@pb/components'
import { BannerNotification } from '@hm-group/fabric-components'
import {
  getLabelFromStorage,
  currentBrand,
  request,
  getLocale,
  getCookie,
  isNorthstarBrand,
  isAkamaiEnabled,
} from '@pb/utils'
import cn from 'classnames'
import { useSelector, useDispatch } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import { RootState, ThunkResult } from 'redux/types'

import { operationFetchFavourites } from '../../duck/operations'
import { getFavouritesItems, getIsFavouritesLoaded } from '../../duck/selectors'
import { NormalizedFavourites } from '../../duck/types'
import FavouritesEmpty from '../FavouritesEmpty'
import {
  FavouritesAddToBagVerification,
  FavouritesHiddenItems,
  FavouritesProductGrid,
} from './components'
import styles from './FavouritesItemList.scss'
import { useMapFavouritesItems } from './helpers'
import { FavouritesItemsListProps } from './types'

const FavouritesItemsList: React.FC<FavouritesItemsListProps> = ({
  shouldSendTrackingOnLoad = false,
}) => {
  const shouldEnabledNS = isNorthstarBrand && process.env.NODE_ENVIRONMENT === 'development'
  const [isNsNibLoading, setIsNsNibLoading] = useState<boolean>(false)
  const [userCookie, setUserCookie] = useState<{ email: string; bpid: string }>({
    email: '',
    bpid: '',
  })
  const [isNsNibSubmitSuccessful, setIsNsNibSubmitSuccessful] = useState<boolean | null>(null)

  const dispatch = useDispatch()

  const fetchFavourites = (): ThunkResult<void> =>
    dispatch(operationFetchFavourites(shouldSendTrackingOnLoad))
  const favouritesItems = useSelector((state: RootState) => getFavouritesItems(state))

  const isFavouritesLoaded = useSelector((state: RootState) => getIsFavouritesLoaded(state))

  const isStyleboardHybrisEnabled = useSelector(appSelectors.getIsStyleboardEnabled)

  const isStyleboardsEnabled =
    process.env.IS_STYLEBOARD_ENABLED === 'enabled' && isStyleboardHybrisEnabled

  const notificationBarBelowTotalItems =
    process.env.FAVOURITES_NOTIFICATION_BAR_PLACEMENT === 'below'

  const isReactBackToTopEnabled = process.env.IS_REACT_BACK_TO_TOP_ENABLED === 'enabled'

  const isNotInStock = favouritesItems.find(
    (favouritesItem: NormalizedFavourites) => favouritesItem.showOutOfStockImageOverlay === true,
  )

  const getUserInfo = (): void => {
    const dataInJSON = JSON.parse(getCookie('userCookie') || '{}')
    setUserCookie({ email: dataInJSON?.hybris_uuid || '', bpid: dataInJSON?.bpid || '' })
  }

  useEffect(() => {
    getUserInfo()
    fetchFavourites()
  }, [])
  const appWrapperClass = `${currentBrand}AppWrapper`
  const siteBrand = `${currentBrand}Brand`

  const productTileData = useMapFavouritesItems(favouritesItems, styles.articleContainer)

  const i18nNotifyIfBackLabels = window?.hm?.i18n?.notifyIfBack

  const isAkamaiImageEnabled = isAkamaiEnabled()

  const nibNsLabels = {
    labelErrorHeading: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.error?.heading_text
      : getLabelFromStorage('notifyIfBack.modal.error.heading_text'),
    labelErrorInfo: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.error?.info_text
      : getLabelFromStorage('notifyIfBack.modal.error.info_text'),
    labelErrorButton: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.error?.button_text
      : getLabelFromStorage('notifyIfBack.modal.error.button_text'),
    labelSuccessHeading: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.success?.heading_text
      : getLabelFromStorage('notifyIfBack.modal.success.heading_text'),
    labelSuccessButton: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.success?.button_text
      : getLabelFromStorage('notifyIfBack.modal.success.button_text'),
    labelSelectHeading: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.heading_text
      : getLabelFromStorage('notifyIfBack.modal.select.heading_text'),
    labelSelectInfo: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.info_text
      : getLabelFromStorage('notifyIfBack.modal.select.info_text'),
    labelSelectEmail: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.emailLabel_text
      : getLabelFromStorage('notifyIfBack.modal.select.emailLabel_text'),
    labelSelectSize: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.selectSize_text
      : getLabelFromStorage('notifyIfBack.modal.select.selectSize_text'),
    labelWarningFull: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.warningInfoFull_text
      : getLabelFromStorage('notifyIfBack.modal.select.warningInfoFull_text'),
    labelWarningPhone: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.warningInfoPhone_text
      : getLabelFromStorage('notifyIfBack.modal.select.warningInfoPhone_text'),
    labelWarningEmail: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.warningInfoEmail_text
      : getLabelFromStorage('notifyIfBack.modal.select.warningInfoEmail_text'),
    labelWarningPrivacy: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.select?.privacyLink_text
      : getLabelFromStorage('notifyIfBack.modal.select.privacyLink_text'),
    labelNotifyMe: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.notifyMeButton_text
      : getLabelFromStorage('notifyIfBack.notifyMeButton_text'),
    labelLoginInfo: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.login?.info_text
      : getLabelFromStorage('notifyIfBack.modal.login.info_text'),
    labelLoginText: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.login?.text_text
      : getLabelFromStorage('notifyIfBack.modal.login.text_text'),
    labelLoginButton: i18nNotifyIfBackLabels
      ? i18nNotifyIfBackLabels?.modal?.login?.button_text
      : getLabelFromStorage('notifyIfBack.modal.login.button_text'),
    labelClose: window?.hm?.i18n?.general?.close
      ? window?.hm.i18n?.general?.close
      : getLabelFromStorage('general.close'),
  }

  const handleSubmitNib = async (data: {}): Promise<void> => {
    const locale = getLocale()
    setIsNsNibLoading(true)
    if (process.env.NS_NIB_API_URL) {
      try {
        await request.post(process.env.NS_NIB_API_URL, {
          data: { ...data, locale },
        })
        setIsNsNibSubmitSuccessful(true)
      } catch (error) {
        setIsNsNibSubmitSuccessful(false)
      }
    } else {
      setIsNsNibSubmitSuccessful(false)
    }
    setIsNsNibLoading(false)
  }

  return (
    <FadeSwap
      flag={!isFavouritesLoaded}
      placeholder={
        <ProductGrid
          isLoading
          productTileClassName={styles.articleContainer}
          loaderData={{ numberOfItems: 8, showLoaderCta: true }}
          className={cn(!isStyleboardsEnabled && styles.mobilePadding, styles.loader)}
        />
      }
      className={cn(styles[siteBrand], favouritesItems.length === 0 && styles.favouriteEmpty)}
    >
      <FavouritesAddToBagVerification />
      {favouritesItems.length > 0 ? (
        <>
          <div className={cn(notificationBarBelowTotalItems && styles.notificationBarPlacement)}>
            {isNotInStock &&
              (shouldEnabledNS ? (
                <BannerNotification
                  closeLabel="close"
                  message={getLabelFromStorage('favorites.outofstock')}
                  severity="warning"
                  shouldHideCloseButton={false}
                >
                  {getLabelFromStorage('favorites.outofstock')}
                </BannerNotification>
              ) : (
                <Notification
                  shouldIgnoreTimeSpan
                  className={styles.oosStatusMessage}
                  contentContainerWidth={
                    process.env.IS_NOTIFICATION_BAR_SMALL_CONTAINER_WIDTH_ENABLED === 'enabled'
                      ? 'small'
                      : 'large'
                  }
                  type="warning"
                  isPortalEnabled={false}
                  additionalClass={cn(
                    styles[appWrapperClass],
                    styles.appWrapper,
                    isStyleboardsEnabled && styles.fitToTabContainer,
                    notificationBarBelowTotalItems && styles.notificationBarMarginZero,
                  )}
                  additionalStatusMessageClass={styles.statusMessage}
                >
                  {getLabelFromStorage('favorites.outofstock')}
                </Notification>
              ))}
            {!shouldEnabledNS && (
              <BodyText
                aria-live="polite"
                role="region"
                aria-atomic="true"
                className={cn(
                  styles.favouritesItemsLength,
                  styles.totalItemsText,
                  process.env.FAVOURITES_TOTAL_ITEMS_TEXT_ALIGNMENT === 'center' &&
                    styles.textCenter,
                )}
              >
                {`${favouritesItems.length} `}
                {favouritesItems.length !== 1
                  ? getLabelFromStorage('favorites.bag.items')
                  : getLabelFromStorage('favorites.bag.item')}
              </BodyText>
            )}
          </div>
          <FavouritesProductGrid
            className={cn(!isStyleboardsEnabled && styles.mobilePadding)}
            productTileClassName={styles.articleContainer}
            products={productTileData}
            nibNsLabels={nibNsLabels}
            isSetNotifyIfBackLoading={isNsNibLoading}
            isSetNotifyIfBackSuccessful={isNsNibSubmitSuccessful}
            userData={userCookie}
            onClickSigninNotifyIfBack={() => {
              if (isNorthstarBrand) {
                window?.dispatchEvent(new Event('HEADER_OPEN_SIGN_IN_MODAL'))
              } else {
                window?.hm?.turnToService?.redirectToLogin()
              }
            }}
            setIsSetNotifyIfBackSuccessful={setIsNsNibSubmitSuccessful}
            setIsShowNotifyIfBackModal={() => {}}
            onSubmitNotifyIfBack={handleSubmitNib}
            shouldUseAkamai={isAkamaiImageEnabled}
            shouldShowSkeleton={false}
          />
          <FavouritesHiddenItems className={styles.srOnly} />
          {isReactBackToTopEnabled && (
            <BackToTop label={getLabelFromStorage('back.to.top.button')} />
          )}
        </>
      ) : (
        <FavouritesEmpty />
      )}
    </FadeSwap>
  )
}

export default FavouritesItemsList
